import { TwoUIProgressView } from "@amx/ux-twoui-telemetry";
import { useTheme } from "@amx/ux-twoui-theming";
import { DialogFooter as FluentDialogFooter, Stack as FluentStack } from "@fluentui/react";
import React from "react";
import { Button } from "../../../Buttons/Button/Button";
import { RichText } from "../../../RichText/RichText";
import { Stack } from "../../../Stacks/Stack/Stack";
import { getFooterContentStyles } from "../../Dialog.styles";
import { DialogFooterStyle, DialogSuccessOrErrorProps } from "../../Dialog.types";
import { DialogFooterProps, DialogFooterWithOperationProps } from "./DialogFooter.types";

export const DialogFooter: React.FC<DialogFooterProps> = ({
    isFooterDisabled,
    richTextProps,
    textAlignment = "left",
    removeMarginTop,
    hasFullWidthButtons,
    hasFooterBackground,
    ...otherProps
}) => {
    let buttons: React.ReactNode;

    if (otherProps.dialogType === "secondary") {
        buttons = [
            otherProps.buttonLeft && (<Button {...otherProps.buttonLeft} disabled={isFooterDisabled || otherProps.buttonLeft.disabled} key={1} />),
            <Button {...otherProps.buttonRight} disabled={isFooterDisabled || otherProps.buttonRight.disabled} key={0} />,
        ];
    } else {
        const isPrimaryDisabled = isFooterDisabled || otherProps.buttonPrimary.disabled;
        const isSecondaryDisabled = isFooterDisabled || otherProps.buttonSecondary?.disabled;

        if (otherProps.dialogType === "flow") {
            buttons = [
                <Button {...otherProps.buttonSecondary} disabled={isSecondaryDisabled} key={0} />,
                <Button {...otherProps.buttonPrimary} disabled={isPrimaryDisabled} type="primary" key={1} />
            ];
        } else {
            buttons = [
                <Button {...otherProps.buttonPrimary} disabled={isPrimaryDisabled} type="primary" key={0} />,
                otherProps.buttonSecondary && (<Button {...otherProps.buttonSecondary} disabled={isSecondaryDisabled} key={1} />)
            ];
        }
    }

    //to account for the shifting done by fluent
    const marginLeft = hasFullWidthButtons ? "0px" : undefined;
    const marginRight = hasFullWidthButtons ? "0px" : undefined;
    const marginTop = removeMarginTop ? "0px" : undefined;
    const display = hasFullWidthButtons ? "flex" : undefined;
    const flexDirection = hasFullWidthButtons ? "column" : undefined;
    return (
        <Stack textAlignment={textAlignment} padding={richTextProps && "12px 0 0 0"} childrenGap={12}>
            {richTextProps && <RichText {...richTextProps} />}
            <FluentDialogFooter
                styles={
                    {
                        actions: { marginTop, marginLeft, display, flexDirection },
                        actionsRight: { marginRight, display: "flex", flexDirection, justifyContent: "flex-end" },
                        action: { marginLeft, marginRight, display, flexDirection }
                    }
                }
            >
                {buttons}
            </FluentDialogFooter>
        </Stack>
    );
};

export const DialogFooterWithOperation: React.FC<DialogFooterWithOperationProps> = ({
    operationData,
    footerProps
}) => {
    const theme = useTheme();

    const dialogFooterStyle: DialogFooterStyle = { isStickyFooterWithText: !!footerProps?.richTextProps, hasFooterBackground: footerProps?.hasFooterBackground, hasFooterHorizontalLine: footerProps?.hasFooterHorizontalLine };
    const footerStyleSets = getFooterContentStyles(theme, dialogFooterStyle).footer;

    const footer = (props?: DialogFooterProps) => (
        <>
            {props && (
                <FluentStack className={footerStyleSets}>
                    <DialogFooter {...props} />
                </FluentStack>
            )}
        </>
    );

    const defaultState = footer(footerProps);
    const dialogFooterProps = footerProps? { isFooterDisabled: true, ...footerProps } : undefined;
    if (operationData) {
        const progressState = footer(operationData.progressDialogProps ? operationData.progressDialogProps.dialogFooterProps : dialogFooterProps);
        const formattedState = (props: DialogSuccessOrErrorProps) => footer(props.dialogFooterProps);

        return (
            <TwoUIProgressView
                operationData={operationData.progressViewOperationData}
                inertView={(
                    defaultState
                )}
                firstLoadView={(
                    progressState
                )}
                updatingView={(
                    progressState
                )}
                updateFailedView={(
                    formattedState(operationData.errorDialogProps)
                )}
                finishedView={(
                    operationData.successDialogProps ? formattedState(operationData.successDialogProps) : defaultState
                )}
                failedView={(
                    formattedState(operationData.errorDialogProps)
                )}
            />
        );
    } else {
        return defaultState;
    }
};
